import { formatValue } from './itemValueDisplay';
import { actorLabel } from '../../utils/history';

/**
 * The one judgement both item write paths make about a 409 `stale_item`
 * (keepr-api SCHEMA.md § Concurrency on PUT): the API refused the save
 * because somebody wrote the item since this browser read it, and handed
 * back the item AS IT STANDS. What matters is not THAT it moved but WHICH
 * values moved, measured against the copy this browser read:
 *
 *   - a value only they changed        → take theirs, nothing to ask;
 *   - a value only I changed           → keep mine, nothing to ask;
 *   - a value we both changed, apart   → a conflict: the person decides.
 *
 * The card table's cell edit (`useCellEdit`) asks it of the ONE field it
 * edits; the item form (`ItemForm`) asks it of every field on the card.
 */

// JSON with object keys sorted: a measurement the API hands back with its
// keys in another order is the same value.
const stableJson = value => JSON.stringify(value, (key, v) => (v && typeof v === 'object' && !Array.isArray(v)
	? Object.keys(v).sort().reduce((acc, k) => { acc[k] = v[k]; return acc; }, {})
	: v));

const isBlank = v => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** The same stored value? Every empty spelling ('', null, [], absent) is one value. */
export const sameValue = (a, b) => {
	if (isBlank(a) && isBlank(b)) return true;
	try { return stableJson(a) === stableJson(b); } catch { return false; }
};

/**
 * Sort `names` by who changed them since `base` (the values as this browser
 * read them): `mine` is what the form holds now, `theirs` what the server
 * holds now. Returns `{ theirsOnly, conflicts }` — names only they moved,
 * and names we both moved to DIFFERENT values. A name both moved to the
 * same value is neither: there is nothing to decide.
 */
export function sortStaleFields({ names = [], base = {}, mine = {}, theirs = {} }) {
	const theirsOnly = [];
	const conflicts = [];
	names.forEach(name => {
		const mineMoved = !sameValue(mine?.[name], base?.[name]);
		const theirsMoved = !sameValue(theirs?.[name], base?.[name]);
		if (!theirsMoved) return;
		if (!mineMoved) { theirsOnly.push(name); return; }
		if (!sameValue(mine?.[name], theirs?.[name])) conflicts.push(name);
	});
	return { theirsOnly, conflicts };
}

/** A value as the conflict dialogs quote it: the element's own formatting, "(empty)" for nothing. */
export function conflictValueText(def, value) {
	let text = '';
	try { text = def ? formatValue(def, value) : ''; } catch { text = ''; }
	if (!text && value !== undefined && value !== null && value !== '' && typeof value !== 'object') text = String(value);
	return text || '(empty)';
}

/**
 * WHO changed each conflicting field, and when — read from the item's own
 * change log (`GET /api/items/{id}/history`, keepr-api SCHEMA.md § History),
 * the one record that names the actor of every write. Not `item.updatedBy`:
 * that names the last PERSON to save, and an automation's write moves the
 * item without moving it, so it would name the wrong one.
 *
 * `rows` are history entries, newest first; `conflicts` carry `name` and an
 * optional `path` (default `elements.<name>`; the form's Notes row is
 * `notes`). For each field the NEWEST entry that changed its path is the
 * write that put "theirs" there. Returns `{ [name]: { who, at, isMe } }`,
 * leaving out a field no readable entry explains (the caller says
 * "Someone" then). `me` is the signed-in account id: a change of one's own
 * — another tab, another device — reads "You".
 */
export function conflictAuthors(rows = [], conflicts = [], { me = null, label = defaultActorLabel } = {}) {
	const out = {};
	const wanted = new Map(conflicts.map(entry => [entry.path || `elements.${entry.name}`, entry.name]));
	(rows || []).forEach(row => {
		(row?.changes || []).forEach(change => {
			const name = wanted.get(change?.path);
			if (!name || out[name]) return;
			const actor = row.actor || {};
			const isMe = Boolean(me) && actor.userId != null && String(actor.userId) === String(me)
				&& ['user', 'api-key', 'oauth'].includes(actor.type);
			out[name] = { who: label(actor, { isMe }), at: row.at || null, isMe };
		});
	});
	return out;
}

// The History view's own wording (utils/history actorLabel), with "You" for
// the reader's own account and a connected assistant said as such.
function defaultActorLabel(actor, { isMe }) {
	const named = isMe ? { ...actor, user: { fullName: 'You' } } : actor;
	if (actor?.type === 'oauth') {
		const name = named?.user?.fullName || [named?.user?.firstName, named?.user?.lastName].filter(Boolean).join(' ');
		return name ? `${name} (via a connected assistant)` : 'A connected assistant';
	}
	return actorLabel(named);
}
